import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PROVINCIES, OP_ZOEK_NAAR } from "@/lib/jobOptions";
import BedrijfAanbodFields from "@/components/BedrijfAanbodFields";

export function ProfileForm({ user, profile, setProfile, saveProfile, savingProfile }) {
  return (
    <div className="max-w-xl space-y-4 bg-white border border-slate-200 rounded-md p-6">
      <div>
        <Label className="text-xs font-bold text-slate-700">E-mailadres</Label>
        <Input data-testid="profiel-email" value={user?.email || ""} disabled
          className="rounded-sm border-slate-300 bg-slate-50 text-slate-500" />
        <p className="text-[11px] text-slate-400 mt-1">Je e-mailadres kan niet worden gewijzigd.</p>
      </div>
      <div>
        <Label className="text-xs font-bold text-slate-700">{user?.role === "bedrijf" ? "Contactpersoon" : "Volledige naam"}</Label>
        <Input data-testid="profiel-name" value={profile.name || ""} onChange={(e) => setProfile({ ...profile, name: e.target.value })}
          className="rounded-sm border-slate-300" />
      </div>
      {user?.role === "bedrijf" && (
        <>
          <div>
            <Label className="text-xs font-bold text-slate-700">Bedrijfsnaam</Label>
            <Input data-testid="profiel-bedrijfsnaam" value={profile.bedrijfsnaam || ""} onChange={(e) => setProfile({ ...profile, bedrijfsnaam: e.target.value })}
              className="rounded-sm border-slate-300" />
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">BTW-nummer</Label>
            <Input data-testid="profiel-btw" value={profile.btw_nummer || ""} onChange={(e) => setProfile({ ...profile, btw_nummer: e.target.value })}
              className="rounded-sm border-slate-300" placeholder="bv. NL123456789B01" />
            <p className="text-[11px] text-slate-400 mt-1">Bij wijziging wordt het nieuwe BTW-nummer opnieuw via VIES gecontroleerd.</p>
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">Website</Label>
            <Input data-testid="profiel-website" type="url" value={profile.website || ""} onChange={(e) => setProfile({ ...profile, website: e.target.value })}
              className="rounded-sm border-slate-300" placeholder="https://www.jouwbedrijf.be" />
          </div>
        </>
      )}
      {user?.role === "particulier" && (
        <div>
          <Label className="text-xs font-bold text-slate-700">Adres</Label>
          <Input data-testid="profiel-adres" value={profile.adres || ""} onChange={(e) => setProfile({ ...profile, adres: e.target.value })}
            className="rounded-sm border-slate-300" placeholder="Straat 1, 1234 AB Plaats" />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-bold text-slate-700">Telefoon</Label>
          <Input data-testid="profiel-telefoon" value={profile.telefoon || ""} onChange={(e) => setProfile({ ...profile, telefoon: e.target.value })}
            className="rounded-sm border-slate-300" placeholder="06..." />
        </div>
        <div>
          <Label className="text-xs font-bold text-slate-700">Regio</Label>
          <Input data-testid="profiel-regio" value={profile.regio || ""} onChange={(e) => setProfile({ ...profile, regio: e.target.value })}
            className="rounded-sm border-slate-300" placeholder="bv. Utrecht" />
        </div>
      </div>
      <div>
        <Label className="text-xs font-bold text-slate-700">Provincie</Label>
        <Select value={profile.provincie || ""} onValueChange={(v) => setProfile({ ...profile, provincie: v })}>
          <SelectTrigger data-testid="profiel-provincie" className="rounded-sm border-slate-300"><SelectValue placeholder="Kies provincie" /></SelectTrigger>
          <SelectContent>
            {PROVINCIES.map((g) => (
              <div key={g.groep}>
                <div className="px-2 py-1.5 text-xs font-bold text-slate-400 uppercase tracking-wide">{g.groep}</div>
                {g.opties.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </div>
            ))}
          </SelectContent>
        </Select>
      </div>
      {user?.role === "particulier" && (
        <div data-testid="profiel-op-zoek-naar">
          <Label className="text-xs font-bold text-slate-700 mb-2 block">Ik ben op zoek naar</Label>
          <div className="space-y-2 border border-slate-200 rounded-sm p-3">
            {OP_ZOEK_NAAR.map((opt) => (
              <label key={opt} className="flex items-start gap-2.5 cursor-pointer text-sm text-slate-700">
                <input type="checkbox"
                  checked={(profile.op_zoek_naar || []).includes(opt)}
                  onChange={() => setProfile((p) => ({
                    ...p,
                    op_zoek_naar: (p.op_zoek_naar || []).includes(opt)
                      ? p.op_zoek_naar.filter((x) => x !== opt)
                      : [...(p.op_zoek_naar || []), opt],
                  }))}
                  className="mt-0.5 w-4 h-4 accent-orange-500 flex-shrink-0" />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        </div>
      )}
      {user?.role === "bedrijf" && (
        <div className="pt-2 border-t border-slate-200">
          <BedrijfAanbodFields data={profile} onChange={setProfile} />
          <label className="flex items-start gap-2.5 cursor-pointer bg-slate-50 border border-slate-200 rounded-md px-4 py-3 mt-5" data-testid="profiel-plaatst-voor-collega-wrap">
            <input type="checkbox" data-testid="profiel-plaatst-voor-collega"
              checked={!!profile.plaatst_voor_collega}
              onChange={(e) => setProfile({ ...profile, plaatst_voor_collega: e.target.checked })}
              className="mt-0.5 w-4 h-4 accent-orange-500 flex-shrink-0" />
            <span className="text-sm text-slate-700">
              <strong>Ik plaats ook voor collega's</strong> — hiermee kun je zelf ook als professional op opdrachten reageren (tab "Reageren op opdrachten").
            </span>
          </label>
        </div>
      )}
      <Button data-testid="save-profiel-btn" onClick={saveProfile} disabled={savingProfile}
        className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold h-11 w-full">
        {savingProfile ? "Bezig..." : "Profiel opslaan"}
      </Button>
    </div>
  );
}
